import { jsPDF } from 'jspdf';
import {
  PAGE_WIDTH_INCHES,
  PAGE_HEIGHT_INCHES,
  RENDER_DPI,
  getLayoutInPixels,
  MIN_FONT_SIZE
} from './worksheetLayout';
import { measureTextWidth } from './fontCalibration';

// Font family for tracing
const TRACING_FONT = 'SsTraceTogether';

/**
 * Load font as base64 for embedding in PDF
 */
const loadFontAsBase64 = async (fontUrl) => {
  const response = await fetch(fontUrl);
  const blob = await response.blob();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove the data URL prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Load image as base64
 */
const loadImageAsBase64 = async (imageUrl) => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Calculate font size and scale factor to match preview exactly
 * Uses the same calculation as WorksheetPreview.jsx
 */
const calculateScaleFactor = (layout, name) => {
  const row = layout.rows[0];
  const lineHeightPx = row.lineHeightPx;
  const availableWidthPx = row.widthPx * 0.98; // 2% margin like preview
  
  // Base font size that fills the line height (matching preview's 1.56 multiplier - 10% larger)
  const baseFontSizePx = lineHeightPx * 1.56;
  
  if (!name.trim()) return { scaleFactor: 1, fontSizePx: baseFontSizePx };
  
  // Measure text width at base size
  const textWidthPx = measureTextWidth(name, baseFontSizePx, TRACING_FONT);
  
  // Calculate scale factor if text exceeds width
  if (textWidthPx > availableWidthPx) {
    const scaleFactor = Math.max(availableWidthPx / textWidthPx, 0.25);
    return { scaleFactor, fontSizePx: baseFontSizePx * scaleFactor };
  }
  
  return { scaleFactor: 1, fontSizePx: baseFontSizePx };
};

/**
 * Get scaled line positions for a row (lines scale with text)
 */
const getScaledRowLines = (row, scaleFactor) => {
  const centerY = (row.topLine + row.bottomLine) / 2;
  const halfHeight = (row.bottomLine - row.topLine) / 2;
  const scaledHalfHeight = halfHeight * scaleFactor;
  
  return {
    topLine: centerY - scaledHalfHeight,
    midLine: centerY,
    bottomLine: centerY + scaledHalfHeight
  };
};

/**
 * Generate PDF with worksheet
 * Draws guide lines and text to match the live preview exactly
 * 
 * @param {string} name - Child's name
 * @param {string} templatePath - Path to template image
 * @param {string} childName - Name for filename
 * @returns {Promise<void>}
 */
export const generatePDF = async (name, templatePath, childName = 'worksheet') => {
  // Create PDF in letter size
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter' // 8.5 x 11 inches
  });
  
  try {
    // Load and embed custom font
    const fontBase64 = await loadFontAsBase64('/fonts/SsTraceTogether-Regular.ttf');
    pdf.addFileToVFS('SsTraceTogether-Regular.ttf', fontBase64);
    pdf.addFont('SsTraceTogether-Regular.ttf', TRACING_FONT, 'normal');
    
    // Load template image
    const templateBase64 = await loadImageAsBase64(templatePath);
    
    // Add template as background (full page)
    pdf.addImage(templateBase64, 'PNG', 0, 0, PAGE_WIDTH_INCHES, PAGE_HEIGHT_INCHES);
    
    // Get layout (same calculations as preview)
    const layout = getLayoutInPixels(RENDER_DPI);
    
    // Calculate scale factor for text and lines
    const { scaleFactor, fontSizePx } = calculateScaleFactor(layout, name);
    
    // Draw scaled guide lines for each row (matching preview)
    const solidLineWidth = 0.012 * scaleFactor;
    const dashedLineWidth = 0.009 * scaleFactor;
    
    for (const rowData of layout.rows) {
      const leftInches = rowData.left;
      const widthInches = rowData.width;
      
      // Get scaled line positions
      const scaledLines = getScaledRowLines(rowData, scaleFactor);
      
      // Top line - solid
      pdf.setDrawColor(102, 102, 102); // #666666
      pdf.setLineWidth(solidLineWidth);
      pdf.setLineDashPattern([], 0);
      pdf.line(leftInches, scaledLines.topLine, leftInches + widthInches, scaledLines.topLine);
      
      // Mid line - dashed
      pdf.setDrawColor(153, 153, 153); // #999999
      pdf.setLineWidth(dashedLineWidth);
      pdf.setLineDashPattern([0.05 * scaleFactor, 0.04 * scaleFactor], 0);
      pdf.line(leftInches, scaledLines.midLine, leftInches + widthInches, scaledLines.midLine);
      
      // Bottom line - solid
      pdf.setDrawColor(102, 102, 102); // #666666
      pdf.setLineWidth(solidLineWidth);
      pdf.setLineDashPattern([], 0);
      pdf.line(leftInches, scaledLines.bottomLine, leftInches + widthInches, scaledLines.bottomLine);
    }
    
    // Convert font size to points for PDF (1 inch = 72 points)
    const fontSizePoints = fontSizePx * (72 / RENDER_DPI);
    
    // Set font
    pdf.setFont(TRACING_FONT, 'normal');
    pdf.setFontSize(fontSizePoints);
    pdf.setTextColor(51, 51, 51); // #333333
    
    // Draw name text on each row using scaled positions
    if (name.trim()) {
      for (const rowData of layout.rows) {
        const leftInches = rowData.left;
        const widthInches = rowData.width;
        
        // Get scaled line positions
        const scaledLines = getScaledRowLines(rowData, scaleFactor);
        
        // Measure text to center it
        const textWidthInches = pdf.getTextWidth(name);
        const centerX = leftInches + (widthInches - textWidthInches) / 2;
        
        // Calculate text position to match preview exactly
        // Preview has double offset effect:
        // 1. alignItems: flex-end + div height causes ~8% offset
        // 2. transform: 'translateY(8%)' adds another ~8%
        // Total: ~16% below the bottom guide line
        const rowHeight = scaledLines.bottomLine - scaledLines.topLine;
        const translateOffset = rowHeight * 0.16;
        
        // Position text baseline at bottom line + the translate offset
        const textY = scaledLines.bottomLine + translateOffset;
        
        pdf.text(name, centerX, textY);
      }
    }
    
    // Save PDF
    const fileName = `${childName.replace(/[^a-zA-Z0-9]/g, '_')}_tracing_worksheet.pdf`;
    pdf.save(fileName);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF and return as blob (for preview)
 */
export const generatePDFBlob = async (name, templatePath) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter'
  });
  
  try {
    // Load and embed custom font
    const fontBase64 = await loadFontAsBase64('/fonts/SsTraceTogether-Regular.ttf');
    pdf.addFileToVFS('SsTraceTogether-Regular.ttf', fontBase64);
    pdf.addFont('SsTraceTogether-Regular.ttf', TRACING_FONT, 'normal');
    
    // Load template image
    const templateBase64 = await loadImageAsBase64(templatePath);
    pdf.addImage(templateBase64, 'PNG', 0, 0, PAGE_WIDTH_INCHES, PAGE_HEIGHT_INCHES);
    
    // Get layout
    const layout = getLayoutInPixels(RENDER_DPI);
    
    // Calculate scale factor for text and lines
    const { scaleFactor, fontSizePx } = calculateScaleFactor(layout, name);
    
    // Draw scaled guide lines for each row
    const solidLineWidth = 0.012 * scaleFactor;
    const dashedLineWidth = 0.009 * scaleFactor;
    
    for (const rowData of layout.rows) {
      const leftInches = rowData.left;
      const widthInches = rowData.width;
      const scaledLines = getScaledRowLines(rowData, scaleFactor);
      
      // Top line - solid
      pdf.setDrawColor(102, 102, 102);
      pdf.setLineWidth(solidLineWidth);
      pdf.setLineDashPattern([], 0);
      pdf.line(leftInches, scaledLines.topLine, leftInches + widthInches, scaledLines.topLine);
      
      // Mid line - dashed
      pdf.setDrawColor(153, 153, 153);
      pdf.setLineWidth(dashedLineWidth);
      pdf.setLineDashPattern([0.05 * scaleFactor, 0.04 * scaleFactor], 0);
      pdf.line(leftInches, scaledLines.midLine, leftInches + widthInches, scaledLines.midLine);
      
      // Bottom line - solid
      pdf.setDrawColor(102, 102, 102);
      pdf.setLineWidth(solidLineWidth);
      pdf.setLineDashPattern([], 0);
      pdf.line(leftInches, scaledLines.bottomLine, leftInches + widthInches, scaledLines.bottomLine);
    }
    
    const fontSizePoints = fontSizePx * (72 / RENDER_DPI);
    
    pdf.setFont(TRACING_FONT, 'normal');
    pdf.setFontSize(fontSizePoints);
    pdf.setTextColor(51, 51, 51);
    
    // Draw name text on each row using scaled positions
    if (name.trim()) {
      for (const rowData of layout.rows) {
        const leftInches = rowData.left;
        const widthInches = rowData.width;
        const scaledLines = getScaledRowLines(rowData, scaleFactor);
        
        const textWidthInches = pdf.getTextWidth(name);
        const centerX = leftInches + (widthInches - textWidthInches) / 2;
        
        // Calculate text position to match preview exactly
        // Preview has double offset: ~8% from div positioning + 8% from translateY
        const rowHeight = scaledLines.bottomLine - scaledLines.topLine;
        const translateOffset = rowHeight * 0.16;
        const textY = scaledLines.bottomLine + translateOffset;
        
        pdf.text(name, centerX, textY);
      }
    }
    
    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating PDF blob:', error);
    throw error;
  }
};
